'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

export default function HeroSection() {
    const [mounted, setMounted] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!mounted) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animId: number;
        let t = 0;

        const resize = () => {
            canvas.width = canvas.offsetWidth * window.devicePixelRatio;
            canvas.height = canvas.offsetHeight * window.devicePixelRatio;
            ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        };
        resize();
        window.addEventListener('resize', resize);

        const draw = () => {
            const w = canvas.offsetWidth;
            const h = canvas.offsetHeight;
            ctx.clearRect(0, 0, w, h);
            t += 0.003;

            // Animated gradient orbs
            const orbs = [
                { x: w * 0.2 + Math.sin(t) * 60, y: h * 0.3 + Math.cos(t * 0.7) * 40, r: 280, color: 'rgba(55,87,255,0.07)' },
                { x: w * 0.8 + Math.cos(t * 0.8) * 50, y: h * 0.6 + Math.sin(t * 1.1) * 50, r: 320, color: 'rgba(55,87,255,0.05)' },
                { x: w * 0.5 + Math.sin(t * 1.3) * 80, y: h * 0.15 + Math.cos(t * 0.6) * 30, r: 200, color: 'rgba(100,130,255,0.06)' },
            ];

            orbs.forEach(orb => {
                const grad = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.r);
                grad.addColorStop(0, orb.color);
                grad.addColorStop(1, 'transparent');
                ctx.fillStyle = grad;
                ctx.beginPath();
                ctx.arc(orb.x, orb.y, orb.r, 0, Math.PI * 2);
                ctx.fill();
            });

            animId = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener('resize', resize);
        };
    }, [mounted]);

    return (
        <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-24 pb-20 px-6 md:px-10">
            {/* Animated canvas background */}
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ opacity: 1 }}
            />

            {/* Subtle grid */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: `linear-gradient(rgba(55,87,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(55,87,255,0.04) 1px, transparent 1px)`,
                    backgroundSize: '60px 60px',
                }}
            />

            <div className="relative z-10 max-w-5xl mx-auto text-center">
                {/* Label */}
                <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-8"
                    style={{ background: 'rgba(55,87,255,0.08)', color: BRAND, border: '1px solid rgba(55,87,255,0.15)', letterSpacing: '0.15em' }}
                >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: BRAND }} />
                    The connected education platform
                </div>

                {/* Headline */}
                <h1
                    className="font-display font-800 mb-6"
                    style={{
                        fontSize: 'clamp(3rem, 8vw, 7.5rem)',
                        lineHeight: 0.92,
                        letterSpacing: '-0.04em',
                        color: '#0D0D0D',
                    }}
                >
                    Learning works
                    <br />
                    <span style={{ color: BRAND }}>better connected.</span>
                </h1>

                {/* Subtext */}
                <p
                    className="text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto"
                    style={{ color: '#555', fontWeight: 400 }}
                >
                    Wisly brings students, parents, tutors, and institutions into one unified environment — so every part of the learning journey is visible, connected, and working together.
                </p>

                {/* CTAs */}
                <div className="flex flex-wrap items-center justify-center gap-4">
                    <a
                        href="#wisly-learn"
                        className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-base font-700 text-white transition-all duration-300"
                        style={{ background: BRAND, boxShadow: `0 8px 32px rgba(55,87,255,0.3)` }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 16px 48px rgba(55,87,255,0.4)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(55,87,255,0.3)'; }}
                    >
                        Explore Wisly Learn
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </a>
                    <a
                        href="#wisly-studio"
                        className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-base font-600 transition-all duration-300"
                        style={{ color: '#0D0D0D', border: '1.5px solid rgba(13,13,13,0.15)', background: 'transparent' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = BRAND; (e.currentTarget as HTMLElement).style.color = BRAND; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(13,13,13,0.15)'; (e.currentTarget as HTMLElement).style.color = '#0D0D0D'; }}
                    >
                        Explore Wisly Studio
                    </a>
                </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
                <span className="text-xs font-600 uppercase tracking-widest" style={{ color: '#888', letterSpacing: '0.15em' }}>Scroll</span>
                <div className="w-px h-8" style={{ background: 'linear-gradient(to bottom, #888, transparent)' }} />
            </div>
        </section>
    );
}
